// src/components/WarRoomCommand.js
//
// SECTION 544 (2026-09-30). The War Room Bob asked for: the whole fight on one screen. Six views over the committee's
// claims - the Board, the Clocks, the Patterns, the Money, the Carrier record, the Strategy - read from what the General
// Chairperson can already read (src\data\warRoomBoard.js, pure). The only thing written here is a strategy note.
import React, { useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, doc, onSnapshot, query, setDoc, serverTimestamp, where } from 'firebase/firestore';
import { boardCards, clockRows, patterns, money, carrierRecord, notePatch, idOf, labelOf, URGENT_DAYS, NOTE_MAX } from '../data/warRoomBoard';
import { RULING_268_OUTCOME_BOX } from '../data/appealLadder';   // ruling 268: the one outcome box
import { defaultNoticeDraft, patternClaimDraft, boardFacts, DRAFT_MARK } from '../data/warRoomDrafts';   // Record 657: War Room items 3-5
import { activate } from '../data/a11yActivate';   // pass 2: role, tabIndex and Enter/Space from one tested helper

const C = { panel: '#0B1626', card: '#0F2135', line: '#2E4A6B', dim: '#8AA4BF', accent: '#E8A020', bad: '#E06C6C', ok: '#5FB878', text: '#DCE7F2', red: '#C0392B' };
const $ = (n) => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const daysText = (d) => (d < 0 ? (-d) + ' day' + (d === -1 ? '' : 's') + ' overdue' : d === 0 ? 'due today' : d + ' day' + (d === 1 ? '' : 's') + ' left');
const box = { background: C.panel, border: '1px solid ' + C.line, borderRadius: 8, padding: '10px 14px' };
const th = { textAlign: 'left', padding: '5px 8px', color: C.dim, fontSize: 11, borderBottom: '1px solid ' + C.line, whiteSpace: 'nowrap' };
const td = { padding: '5px 8px', fontSize: 12, color: C.text, borderBottom: '1px solid #16283D', verticalAlign: 'top' };

function Tile({ label, value, tone, sub, onClick }) {
  const col = tone === 'bad' ? C.bad : tone === 'warn' ? C.accent : tone === 'ok' ? C.ok : C.text;
  return (
    <div {...activate(onClick, onClick ? label + ': ' + value : '')} style={{ ...box, flex: '1 1 150px', minWidth: 150, cursor: onClick ? 'pointer' : 'default', borderColor: tone === 'bad' ? C.bad : tone === 'warn' ? C.accent : C.line }}>
      <div style={{ fontSize: 11, color: C.dim }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, color: col }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: C.dim }}>{sub}</div> : null}
    </div>
  );
}

// Record 657: a draft to read, copy or save - the platform sends nothing.
function DraftBox({ title, text, onClose, fileName }) {
  const [copied, setCopied] = useState('');
  const copy = async () => { try { await navigator.clipboard.writeText(text); setCopied('Copied.'); } catch (e) { setCopied('Copying was refused - select the text and copy it.'); } };
  const save = () => { try { const u = URL.createObjectURL(new Blob([text], { type: 'text/plain' })); const a = document.createElement('a'); a.href = u; a.download = fileName || 'draft.txt'; a.click(); setTimeout(() => URL.revokeObjectURL(u), 1000); } catch (e) { setCopied('Saving was refused.'); } };
  return (
    <div {...activate((e) => e.stopPropagation())} style={{ ...box, marginTop: 8, borderColor: C.accent }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: C.accent }}>{title}</div>
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>
      </div>
      <textarea aria-label="The text as it stands (read only)" className="form-textarea" readOnly value={text} rows={14} style={{ width: '100%', fontSize: 12, fontFamily: 'monospace' }} />
      <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn btn-primary btn-sm" onClick={copy}>Copy</button>
        <button className="btn btn-secondary btn-sm" onClick={save}>Save as a text file</button>
        <span style={{ fontSize: 11, color: C.dim }}>{copied || DRAFT_MARK}</span>
      </div>
    </div>
  );
}

// ---- 1. the Board
function Board({ b, onOpen }) {
  const cols = b.columns.filter(c => c.key !== 'off' || c.cards.length);
  return (
    <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 8 }}>
      {cols.map(col => (
        <div key={col.key} style={{ minWidth: 210, flex: '0 0 210px', background: '#0A1726', border: '1px solid ' + C.line, borderRadius: 8 }}>
          <div style={{ padding: '8px 10px', borderBottom: '1px solid ' + C.line }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.text }}>{col.label} <span style={{ color: C.dim, fontWeight: 400 }}>({col.cards.length})</span></div>
            <div style={{ fontSize: 10.5, color: C.dim }}>{col.office}</div>
          </div>
          <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
            {col.cards.length === 0 ? <div style={{ fontSize: 11, color: 'var(--steel)' }}>none</div> : col.cards.map(k => (
              <div key={k.id} {...activate(() => onOpen && onOpen(k.id))} title="Open the claim"
                style={{ background: C.card, border: '1px solid ' + (k.overdue ? C.bad : k.urgent ? C.accent : C.line), borderLeft: '4px solid ' + (k.overdue || k.urgent ? C.bad : C.line), borderRadius: 6, padding: '6px 8px', cursor: onOpen ? 'pointer' : 'default' }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: C.text }}>{k.label}</div>
                <div style={{ fontSize: 11.5, color: C.text }}>{k.claimant || <span style={{ color: C.dim }}>(no claimant)</span>}</div>
                <div style={{ fontSize: 10.5, color: C.dim }}>{k.rules.join('; ') || '(no rule)'}</div>
                {col.key === 'board' ? boardFacts(k.claim).map((f, i) => <div key={i} style={{ fontSize: 10.5, color: C.accent }}>{f}</div>) : null}{/* Record 657: docket and award */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 3, fontSize: 11 }}>
                  <span style={{ color: C.text }}>{k.amount !== null ? $(k.amount) : <span style={{ color: C.dim }}>no amount</span>}</span>
                  {k.next ? <span style={{ color: k.urgent ? C.bad : C.dim, fontWeight: k.urgent ? 700 : 400 }}>{daysText(k.next.days)}</span> : <span style={{ color: 'var(--steel)' }}>no clock</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- 2. the Clocks
function Clocks({ rows, onOpen, user, committee }) {
  const [draftFor, setDraftFor] = useState(-1);   // Record 657: the row whose default notice is open
  if (!rows.length) return <div style={{ ...box, fontSize: 12, color: C.dim }}>No live deadline on the committee's claims. A clock runs only where the agreement's time is on file and the act that starts it is recorded on the claim.</div>;
  return (
    <div style={{ ...box, overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}>Due</th><th style={th}>Time left</th><th style={th}>Whose move</th><th style={th}>Deadline</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i}>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>
              {onOpen && r.claimId ? (
                <button type="button" onClick={() => onOpen(r.claimId)}
                  aria-label={'Open the claim due ' + r.date}
                  style={{ color: C.text, textDecoration: 'underline', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>{r.date}</button>
              ) : r.date}
            </td>
            <td style={{ ...td, whiteSpace: 'nowrap', color: r.days < 0 ? C.bad : r.days < URGENT_DAYS ? C.accent : C.text, fontWeight: r.days < URGENT_DAYS ? 700 : 400 }}>{daysText(r.days)}</td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.side === 'carrier' ? 'Carrier' : 'Organization'}</td>
            <td style={td}>{r.title}
              {r.default ? (
                <div style={{ marginTop: 4, color: C.ok, fontWeight: 700 }}>
                  CARRIER DEFAULT? No answer is on the record {r.default.since} day{r.default.since === 1 ? '' : 's'} past its time.
                  <div style={{ fontWeight: 400, color: C.text }}>{r.default.onSilence ? <>The agreement: “{r.default.onSilence}”{r.default.cite ? ' (' + r.default.cite + ')' : ''}</> : 'The platform holds no words from this agreement on what follows the carrier’s silence - read the agreement.'}</div>
                  <div style={{ fontWeight: 400, color: C.dim }}>Confirm no answer was received before relying on it.</div>
                  <button className="btn btn-secondary btn-sm" style={{ marginTop: 4 }} onClick={(e) => { e.stopPropagation(); setDraftFor(draftFor === i ? -1 : i); }}>{draftFor === i ? 'Hide the draft' : 'Draft the default notice'}</button>
                  {draftFor === i ? <DraftBox title="Default notice - a draft" text={defaultNoticeDraft(r, user, committee, new Date().toISOString().slice(0, 10))} onClose={() => setDraftFor(-1)} fileName={'default-notice-' + (r.claimId || 'claim') + '.txt'} /> : null}
                </div>
              ) : null}
            </td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

// ---- 3. the Patterns
function Patterns({ p }) {
  const [open, setOpen] = useState('');   // Record 657: the pattern whose draft is open
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12 }}>
      {p.map(d => (
        <div key={d.key} style={box}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>{d.label}</div>
          {!d.measured ? <div style={{ fontSize: 12, color: C.dim }}>Not measured - no claim records this.</div>
            : d.rows.length === 0 ? <div style={{ fontSize: 12, color: C.dim }}>Nothing repeats yet.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={th}></th><th style={th}>Claims</th><th style={th}>Won</th><th style={th}>Lost</th><th style={th}>Open</th><th style={th}>Claimed</th></tr></thead>
              <tbody>{d.rows.map(r => (
                <tr key={r.key} title={r.claims.map(labelOf).join(', ')}>
                  <td style={td}>{r.label}</td><td style={{ ...td, fontWeight: 700, color: r.n >= 3 ? C.accent : C.text }}>{r.n}</td>
                  <td style={{ ...td, color: C.ok }}>{r.won}</td><td style={{ ...td, color: C.bad }}>{r.lost}</td><td style={td}>{r.open}</td><td style={td}>{$(r.amount)}</td>
                  <td style={td}><button className="btn btn-secondary btn-sm" onClick={() => setOpen(open === d.key + '|' + r.key ? '' : d.key + '|' + r.key)}>Draft</button></td>
                </tr>
              ))}</tbody>
            </table>
          )}
          {d.rows.filter(r => open === d.key + '|' + r.key).map(r => <DraftBox key={r.key} title="Facts for a pattern claim - a draft" text={patternClaimDraft(d.label, r)} onClose={() => setOpen('')} fileName={'pattern-' + d.key + '.txt'} />)}
        </div>
      ))}
    </div>
  );
}

// ---- 4. the Money
function Money({ m }) {
  const t = m.total;
  const table = (title, rows) => (
    <div style={box}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>{title}</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}></th><th style={th}>Claims</th><th style={th}>Claimed</th><th style={th}>Recovered</th><th style={th}>Denied</th><th style={th}>At risk</th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.key}><td style={td}>{r.key}</td><td style={td}>{r.n}</td><td style={td}>{$(r.tally.submitted.amount)}</td>
            <td style={{ ...td, color: C.ok }}>{$(r.tally.awarded.amount)}</td><td style={{ ...td, color: C.bad }}>{$(r.tally.unsuccessful.amount)}</td>
            <td style={{ ...td, color: r.atRisk ? C.bad : C.dim, fontWeight: r.atRisk ? 700 : 400 }}>{$(r.atRisk)}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
  return (
    <>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        <Tile label="Claimed" value={$(t.submitted.amount)} sub={t.submitted.n + ' claims' + (t.submitted.noAmount ? ', ' + t.submitted.noAmount + ' with no amount' : '')} />
        <Tile label="Recovered" value={$(t.awarded.amount)} tone="ok" sub={t.awarded.n + ' won' + (t.awarded.unknownOn ? ', ' + t.awarded.unknownOn + ' with no amount recorded' : '')} />
        <Tile label="Denied" value={$(t.unsuccessful.amount)} tone={t.unsuccessful.n ? 'bad' : null} sub={t.unsuccessful.n + ' lost'} />
        <Tile label="Open" value={t.open.n + t.board.n} sub={t.board.n + ' at the Board'} />
        <Tile label="At risk" value={$(m.atRisk.amount)} tone={m.atRisk.n ? 'bad' : null} sub={m.atRisk.n + ' open claim(s) with an Organization deadline inside ' + URGENT_DAYS + ' days or past'} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 12 }}>
        {table('By carrier', m.byCarrier)}
        {table('By rule', m.byRule)}
      </div>
    </>
  );
}

// ---- 5. the Carrier record
function Carriers({ rows }) {
  if (!rows.length) return <div style={{ ...box, fontSize: 12, color: C.dim }}>No claim names the carrier officer it was presented to yet.</div>;
  return (
    <div style={{ ...box, overflowX: 'auto' }}>
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>Late and past-due are measured against the agreement's answer time from the service date on the claim. A claim without both is counted as not measured.</div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={th}>Carrier officer</th><th style={th}>Carrier</th><th style={th}>Served</th><th style={th}>Denied</th>{RULING_268_OUTCOME_BOX ? <><th style={th}>Partially granted</th><th style={th}>Granted</th><th style={th}>Amount allowed</th></> : <th style={th}>Allowed</th>}<th style={th}>Answered late</th><th style={th}>No answer, past due</th><th style={th}>Awaiting</th><th style={th}>Not measured</th></tr></thead>
        <tbody>{rows.map(r => (
          <tr key={r.officer} title={r.claims.map(labelOf).join(', ')}>
            <td style={{ ...td, fontWeight: 700 }}>{r.officer}</td><td style={td}>{r.carrier}</td><td style={td}>{r.served}</td>
            <td style={{ ...td, color: r.denied ? C.bad : C.text }}>{r.denied}</td>{/* Ruling 268: the three boxes, and the amount allowed with how many answers carried a figure. */}
            {RULING_268_OUTCOME_BOX ? <td style={{ ...td, color: r.partly ? C.accent : C.text }}>{r.partly}</td> : null}
            <td style={{ ...td, color: r.allowed ? C.ok : C.text }}>{r.allowed}</td>
            {RULING_268_OUTCOME_BOX ? <td style={td}>{r.amountAllowedOn ? '$' + r.amountAllowed.toFixed(2) + ' (' + r.amountAllowedOn + ' with a figure)' : '-'}</td> : null}
            <td style={{ ...td, color: r.late ? C.accent : C.text, fontWeight: r.late ? 700 : 400 }}>{r.late}</td>
            <td style={{ ...td, color: r.pastDue ? C.bad : C.text, fontWeight: r.pastDue ? 700 : 400 }}>{r.pastDue}</td>
            <td style={td}>{r.awaiting}</td><td style={{ ...td, color: C.dim }}>{r.notMeasured}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

// ---- 6. the Strategy
function Strategy({ claims, committee, user }) {
  const [notes, setNotes] = useState({});
  const [err, setErr] = useState('');
  const [pick, setPick] = useState('');
  const [form, setForm] = useState({ notes: '', nextMove: '', precedent: '' });
  const [msg, setMsg] = useState('');
  useEffect(() => {
    if (!committee) return undefined;
    const q = query(collection(db, 'warRoomNotes'), where('committee', '==', committee));
    return onSnapshot(q, snap => { const m = {}; snap.docs.forEach(d => { m[d.id] = d.data(); }); setNotes(m); setErr(''); },
      e => setErr('The strategy notes could not be read: ' + (e && e.code ? e.code : e.message)));
  }, [committee]);
  const list = (claims || []).filter(c => c && c.status !== 'draft');
  const cur = list.find(c => idOf(c) === pick) || null;
  useEffect(() => { const n = cur ? notes[idOf(cur)] : null; setForm({ notes: (n && n.notes) || '', nextMove: (n && n.nextMove) || '', precedent: (n && n.precedent) || '' }); setMsg(''); }, [pick]);   // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    const r = notePatch(cur, committee, user, form, cur ? notes[idOf(cur)] : null);
    if (!r.ok) { setMsg(r.errors.join(' ')); return; }
    try { await setDoc(doc(db, 'warRoomNotes', idOf(cur)), { ...r.record, updatedAt: serverTimestamp() }); setMsg('Saved. The earlier version is kept in the note’s history.'); }
    catch (e) { setMsg('Not saved: ' + (e && e.code ? e.code : e.message)); }
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) 2fr', gap: 12 }}>
      <div style={box}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>Claims</div>
        {err ? <div style={{ color: C.bad, fontSize: 12 }}>{err}</div> : null}
        <div style={{ maxHeight: 520, overflowY: 'auto' }}>
          {list.map(c => { const n = notes[idOf(c)]; return (
            <div key={idOf(c)} {...activate(() => setPick(idOf(c)))} style={{ padding: '5px 6px', borderRadius: 4, cursor: 'pointer', fontSize: 12, color: C.text, background: pick === idOf(c) ? '#16304D' : 'transparent' }}>
              {labelOf(c)} <span style={{ color: C.dim }}>{c.claimantName || ''}</span>
              {n ? <div style={{ fontSize: 11, color: C.accent }}>{n.nextMove ? 'Next: ' + n.nextMove.slice(0, 60) : 'note on file'}</div> : null}
            </div>
          ); })}
        </div>
      </div>
      <div style={box}>
        {!cur ? <div style={{ fontSize: 12, color: C.dim }}>Choose a claim. The General Chairperson's working notes, the precedent he relies on and his next move - read and written by the General Chairperson of this committee and the administrator only. Every save keeps the earlier version.</div> : (
          <>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{labelOf(cur)} - {cur.claimantName || ''}</div>
            <div style={{ fontSize: 11, color: C.dim, marginBottom: 8 }}>{[cur.railroad, (cur.matchedRules || []).map(r => r && r.rule).filter(Boolean).join('; '), cur.status].filter(Boolean).join(' · ')}</div>
            <label className="form-label">Next move</label>
            <input aria-label="Next move" className="form-input" value={form.nextMove} maxLength={1000} onChange={e => setForm({ ...form, nextMove: e.target.value })} placeholder="What happens next, and by when" />
            <label className="form-label" style={{ marginTop: 8 }}>Precedent relied on</label>
            <input aria-label="Precedent relied on" className="form-input" value={form.precedent} maxLength={2000} onChange={e => setForm({ ...form, precedent: e.target.value })} placeholder="Award numbers, and what each holds" />
            <label className="form-label" style={{ marginTop: 8 }}>Notes</label>
            <textarea aria-label="Notes" className="form-textarea" rows={8} value={form.notes} maxLength={NOTE_MAX} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="The strengths, the weaknesses, what the carrier will argue, what the claimant must bring" />
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
              <button className="btn btn-primary btn-sm" onClick={save}>Save</button>
              <span style={{ fontSize: 12, color: /^Not saved|Write|Too long|No /.test(msg) ? C.bad : C.ok }}>{msg}</span>
            </div>
            {notes[idOf(cur)] && (notes[idOf(cur)].history || []).length > 1 ? (
              <details style={{ marginTop: 10 }}><summary style={{ cursor: 'pointer', fontSize: 11.5, color: C.dim }}>{notes[idOf(cur)].history.length} versions</summary>
                {notes[idOf(cur)].history.slice().reverse().map((h, i) => <div key={i} style={{ fontSize: 11.5, color: C.dim, padding: '4px 0', borderTop: '1px solid #16283D' }}>{String(h.at).replace('T', ' ').slice(0, 16)} - {h.byName}: {h.nextMove ? 'Next: ' + h.nextMove + '. ' : ''}{(h.notes || '').slice(0, 200)}</div>)}
              </details>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export const TABS = [['board', 'The Board'], ['clocks', 'Clocks'], ['patterns', 'Patterns'], ['money', 'Money'], ['carrier', 'Carrier record'], ['strategy', 'Strategy'], ['meetings', 'Meetings']];

// Record 656 (Bob 2026-10-12: "All" - the War Room's two halves split): view 'board' is the Committee Board (every tab but
// Meetings); view 'meetings' is the War Room proper (its meetings alone, no claims tiles).
export default function WarRoomCommand({ committee, claims, user, tab, setTab, meetings, mayNote, onOpenClaim, view = 'board' }) {
  const t = new Date().toISOString().slice(0, 10);
  const list = useMemo(() => claims || [], [claims]);
  const b = useMemo(() => boardCards(list, t), [list, t]);
  const clocks = useMemo(() => clockRows(list, t), [list, t]);
  const p = useMemo(() => patterns(list), [list]);
  const m = useMemo(() => money(list, t), [list, t]);
  const cr = useMemo(() => carrierRecord(list, t), [list, t]);
  const overdue = clocks.filter(r => r.days < 0).length;
  const week = clocks.filter(r => r.days >= 0 && r.days < URGENT_DAYS).length;
  const repeat = p.reduce((n, d) => n + d.rows.length, 0);
  const lateCarrier = cr.reduce((n, r) => n + r.late + r.pastDue, 0);
  if (view === 'meetings') return <div>{meetings}</div>;
  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <Tile label="Live claims" value={claims === null ? '…' : b.total} sub={committee} onClick={() => setTab('board')} />
        <Tile label="Deadlines inside 7 days" value={week + overdue} tone={overdue ? 'bad' : week ? 'warn' : null} sub={overdue ? overdue + ' overdue' : 'none overdue'} onClick={() => setTab('clocks')} />
        <Tile label="Carrier defaults to check" value={b.defaults} tone={b.defaults ? 'ok' : null} sub="answer time run, no answer on record" onClick={() => setTab('clocks')} />
        <Tile label="Claimed / recovered" value={$(m.total.submitted.amount)} sub={$(m.total.awarded.amount) + ' recovered'} onClick={() => setTab('money')} />
        <Tile label="Repeat patterns" value={repeat} tone={repeat ? 'warn' : null} sub="same rule, place, officer or employee" onClick={() => setTab('patterns')} />
        <Tile label="Carrier late / past due" value={lateCarrier} tone={lateCarrier ? 'warn' : null} sub="answers" onClick={() => setTab('carrier')} />
      </div>
      <div role="tablist" style={{ display: 'flex', gap: 4, flexWrap: 'wrap', borderBottom: '1px solid ' + C.line, marginBottom: 14 }}>
        {TABS.filter(([k]) => (k !== 'strategy' || mayNote) && k !== 'meetings').map(([k, label]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
            style={{ background: tab === k ? C.red : 'transparent', color: tab === k ? '#fff' : C.dim, border: '1px solid ' + (tab === k ? C.red : C.line), borderBottom: 'none', borderRadius: '6px 6px 0 0', padding: '7px 14px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>{label}</button>
        ))}
      </div>
      {claims === null && tab !== 'meetings' ? <div style={{ fontSize: 12, color: C.dim }}>Reading the committee's claims…</div>
        : tab === 'board' ? <Board b={b} onOpen={onOpenClaim} />
        : tab === 'clocks' ? <Clocks rows={clocks} onOpen={onOpenClaim} user={user} committee={committee} />
        : tab === 'patterns' ? <Patterns p={p} />
        : tab === 'money' ? <Money m={m} />
        : tab === 'carrier' ? <Carriers rows={cr} />
        : tab === 'strategy' && mayNote ? <Strategy claims={list} committee={committee} user={user} />
        : meetings}
    </div>
  );
}
